<template>
  <div v-show="questions.length !== 0" class="p-table__content">
    <div class="p-table__content-controller" :style="{ width: controller.width, top: controller.top }">
      <button
        v-if="controller.prev"
        type="button"
        class="p-table__content-controller-prev icon"
        @mousedown="scrollPrevStart" @mouseup="scrollEnd"
      >
        <KkIcon name="prev" color="#3A3B3F" />
      </button>
      <button
        v-if="controller.next"
        type="button"
        class="p-table__content-controller-next icon"
        @mousedown="scrollNextStart" @mouseup="scrollEnd"
      >
        <KkIcon name="next" color="#3A3B3F" />
      </button>
    </div>
    <div ref="container" class="p-table__content-container">
      <table ref="table">
        <thead ref="thead">
          <tr>
            <th class="fixed">
              {{ $t('pages.CompaniesCustomSurveyAnswers.tableHeaderStore') }}
            </th>
            <th>
              {{ $t('pages.CompaniesCustomSurveyAnswers.tableHeaderAnsweredAt') }}
              <span class="p-table__content__sort" :class="`is-${sortKey === 'answered_at' && sortOrder}`" @click="sort('answered_at')" />
            </th>
            <th
              v-for="( question, i ) in questions"
              :key="`question_${i}`"
              class="question"
            >
              <p class="question__label">
                <span class="question__number">{{ `Q${i + 1}.` }}</span>
                <span>({{ $t(`pages.CompaniesCustomSurveyAnswers.questionTypes.${question.type}`, { number: question.options.length }) }})</span>
              </p>
              <div class="question__name">{{ question.name }}</div>
            </th>
            <th v-if="displayEmail">
              {{ $t('pages.CompaniesCustomSurveyAnswers.tableHeaderEmail') }}
            </th>
            <th v-if="showOnlyOwnerAuthority" />
          </tr>
          <tr class="p-table__content__space">
            <td :colspan="numberOfColumns" />
          </tr>
        </thead>
        <tbody ref="tbody">
          <tr
            v-for="(answer, i) in answers"
            :key="`answer_${i}`"
          >
            <td class="fixed">
              {{ answer.storeName }}
            </td>
            <td>
              {{ dateTimeFormat(answer.answeredAt) }}
            </td>

            <td
              v-for="(question, k) in questions"
              :key="`question_answer_${k}`"
            >
              <div
                v-if="answer[`q${question.id}`] == null"
                :class="{ 'noAnswer__nps': question.type === 'nps' }"
              >
                <span>-</span>
              </div>
              <template v-else-if="question.type === 'nps'">
                <div class="p-table__color" :class="`p-table__color--${npsScaleNumber(answer[`q${question.id}`])}`">
                  <span>{{ answer[`q${question.id}`] }}</span>
                </div>
              </template>
              <template v-else-if="['checkbox', 'radio'].includes(question.type)">
                <div v-html="answer[`q${question.id}`].join('<br>')" />
              </template>
              <template v-else>
                <div class="answer__text" v-html="comment(answer[`q${question.id}`])" />
              </template>
            </td>
            <td v-if="displayEmail">
              {{ answer.email ? answer.email : '-' }}
            </td>
            <td v-if="showOnlyOwnerAuthority">
              <div class="deleteButton p-stores__action__item" @click="handleDeleteCustomSurveyAnswer(answer.id)">
                <Icon name="iconCross" class="p-stores__action__icon" />
                {{ $t('labels.delete') }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import { mapState, mapGetters, mapActions } from 'vuex'
import { syntaxHighlight } from '../../modules/syntaxHighlight'
import { searchQueryAnalyzer } from '../../modules/searchQueryAnalyzer'
import { basicDateTimeFormat } from '../../modules/date'
import Icon from '../../components/Icon.vue'
import { KkIcon } from '../../components/base'

export default {
  name: 'CustomSurveyAnswerTable',
  components: {
    Icon,
    KkIcon
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
    answers: {
      type: Array,
      required: true,
    },
    sortKey: {
      type: String,
      required: false,
    },
    sortOrder: {
      type: String,
      required: false,
    },
    hasEmailSetting: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['sort'],
  data() {
    return {
      resizeObserver: null,
      scrollIntervalId: null,
      controller: {
        width: '100%',
        top: '0px',
        targetWidth: '0px',
        prev: false,
        next: false,
        scrollAmount: '0',
      },
    }
  },
  computed: {
    ...mapState({
      loading: state => state.companiesCustomSurveyAnswers.loading,
      accessibleApi: state => state.companiesAccessibleApi.data,
    }),
    ...mapGetters({
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
    }),
    displayEmail() {
      return this.hasEmailSetting || this.answers.some(answer => answer.email)
    },
    numberOfColumns() {
      let count = 2 + this.questions.length
      if (this.displayEmail) count++
      if (this.showOnlyOwnerAuthority) count++
      return count
    },
  },
  mounted() {
    this.tableObserver()
  },
  beforeUnmount() {
    this.resizeObserver.disconnect()
    clearInterval(this.scrollIntervalId)
  },
  methods: {
    ...mapActions({
      deleteCustomSurveyAnswer: 'companiesCustomSurveyAnswers/deleteCustomSurveyAnswer',
    }),
    sort(key) {
      this.$emit('sort', key)
    },
    npsScaleNumber(npsScore) {
      // NPSは10段階評価だが5段階の指標で色を当てるため
      return Math.floor(npsScore / 2)
    },
    dateTimeFormat(date) {
      return basicDateTimeFormat(date)
    },
    comment(text) {
      const t = DOMPurify.sanitize(text).replace(/\n/g, '<br>')
      return this.$route.query['query'] ? syntaxHighlight(t, searchQueryAnalyzer(this.$route.query['query']), 'p-table__highlight') : t
    },
    handleDeleteCustomSurveyAnswer(id) {
      if (confirm(this.$t('pages.CompaniesCustomSurveyAnswers.confirmDelete'))) {
        this.deleteCustomSurveyAnswer(id)
      }
    },
    scrollPrevStart() {
      const $container = this.$refs.container
      this.scrollIntervalId = setInterval(() => {
        if ($container.scrollLeft === 0) {
          this.controller.prev = false
          clearInterval(this.scrollIntervalId)
        } else {
          $container.scrollLeft = $container.scrollLeft - 10
          this.controller.next = true
        }
      }, 10)
    },
    scrollNextStart() {
      const $container = this.$refs.container
      this.scrollIntervalId = setInterval(() => {
        if ($container.scrollLeft >= this.controller.scrollAmount) {
          this.controller.next = false
          clearInterval(this.scrollIntervalId)
        } else {
          $container.scrollLeft = $container.scrollLeft + 10
          this.controller.prev = true
        }
      }, 10)
    },
    scrollEnd() {
      clearInterval(this.scrollIntervalId)
    },
    tableObserver() {
      const $container = this.$refs.container
      const $table = this.$refs.table
      const $thead = this.$refs.thead
      const $tbody = this.$refs.tbody
      if (this.resizeObserver) return
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          console.log(entry)
          if (entry.target.nodeName === 'TABLE') {
            if (!$tbody.firstElementChild) return
            this.controller.top = `${ $thead.offsetHeight }px`
          }
          const $tr = $tbody.firstElementChild
          const $td = $tr.children

          // コントローラー幅の計算
          const controllerWidth = Array.from($td).reduce((prev, current) => {
            return current.classList.contains('fixed') ? prev - current.offsetWidth : prev
          }, $container.offsetWidth)
          this.controller.width = `${ controllerWidth }px`

          // スクロール可能か
          if ($container.clientWidth < $table.clientWidth) this.controller.next = true

          // スクロール可能な量の計算
          const fixedWidth = Array.from($td).reduce((prev, current) => {
            return current.classList.contains('fixed') ? prev + current.offsetWidth : prev
          }, 0)
          this.controller.scrollAmount = $table.clientWidth - fixedWidth - controllerWidth
        }
      })
      this.resizeObserver.observe($table)
      this.resizeObserver.observe($tbody)
      this.resizeObserver.observe($container)
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.p-table__color {
  span {
    min-width: 20px;
    text-align: right;
  }
}

.question {
  &__label {
    position: relative;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__name {
    position: relative;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: normal;
  }

  &__number {
    font-size: 14px;
    font-weight: bold;
    margin-right: 4px;
  }
}

.answer__text {
  width: 370px;
  white-space: normal;
}

.noAnswer__nps {
  text-align: center;
}

.fixed {
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 1;
  white-space: nowrap;
}

.p-table__content {
  position: relative;
  overflow-x: visible;
  &::-webkit-scrollbar {
    display: initial;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #D2D5DB;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  table {
    position: relative;
    z-index: 0;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    th {
      max-width: 150px;
    }
  }

  tbody {
    td {
      white-space: nowrap;
    }
  }
  &-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 84vh;
  }
  &-controller {
    position: absolute;
    right: 0;
    z-index: 3;
    &-prev,
    &-next {
      position: absolute;
      top: 0;
      opacity: .3;
    }
    &-prev {
      left: 0;
      transform: translate(-50%, -50%);
    }
    &-next {
      right: 0;
      transform: translate(50%, -50%);
    }
    @media screen and (max-width: 768px) {
      display: none;
    }
  }
}
.table {
  position: relative;
  table {
    position: relative;
    z-index: 0;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }
  &-container {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 84vh;
  }
  &-controller {
    position: absolute;
    right: 0;
    z-index: 3;
    &-prev,
    &-next {
      position: absolute;
      top: 0;
      opacity: .3;
    }
    &-prev {
      left: 0;
      transform: translate(-50%, -50%);
    }
    &-next {
      right: 0;
      transform: translate(50%, -50%);
    }
    @media screen and (max-width: 768px) {
      display: none;
    }
  }
}

.p-table__color {
  justify-content: center;
}

.deleteButton {
  cursor: pointer;
}
.icon {
  width: 40px;
}
</style>
